import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Divider, Link, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { AgentSessionType } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import {
  Template,
  TemplateContentType,
  TemplateMethod,
} from "features/Influencer/ContactList/schema";
import PortfolioLinkForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/PortfolioLinkForm";
import TemplateCreationLoading from "features/Influencer/Templates/SharedComponents/TemplateCreationLoading";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import UnsavedChanges from "features/Influencer/Templates/TemplatesTable/EditTemplate/components/UnsavedChanges";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { hasSocialMediaProfile } from "utils/influencer";
import { trackEvent } from "utils/tracking";
import { useBulkSend } from "utils/useBulkSend";
import { useGamification } from "utils/useGamification";

import CopywriterOfferDialog from "./CopywriterOfferDialog";
import PickTemplateMethodForm from "./Steps/PickTemplateMethodForm";
import ReviewBentoAiTemplate from "./Steps/ReviewBentoAiTemplate";
import SocialMediaLinksForm from "./Steps/SocialMediaLinksForm";
import CreateTemplateDialogWrapper from "./Wrapper";

interface Props {
  isDialog?: boolean;
}

const CreateTemplateDialog = ({ isDialog = true }: Props) => {
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const location = useLocation();
  const { profile, setProfile } = useContext(OrganizationUserContext);
  const {
    page,
    setPage,
    setCreateTemplateOnboardingDialogOpen,
    setProfileDialogOpen,
    setEmailBrandDialogOpen,
    incompleteProfile,
  } = useContext(OnboardingDialogsContext);
  const { fetchOutreachTemplates, outreachTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const { addTemplateTaskCompleted } = useGamification();
  const { fetchOrganizationBulkSendSettings, fetchNiches } = useBulkSend();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [openIsDirtyWarning, setOpenIsDirtyWarning] = useState<boolean>(false);
  const [openCopywriterOffer, setOpenCopywriterOffer] =
    useState<boolean>(false);
  const [alreadyShownCopywriterOffer, setAlreadyShownCopywriterOffer] =
    useState<boolean>(false);
  const [creatingTemplateLoading, setCreatingTemplateLoading] =
    useState<boolean>(false);

  const isFirstTemplate = !outreachTemplates || outreachTemplates.length === 0;

  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [editTemplate, setEditTemplate] = useState<Template>({
    name: "",
    contentType: TemplateContentType.OUTREACH,
    subject: "",
    body: "",
  });
  const [followUpTemplate, setFollowUpTemplate] = useState<Template>({
    name: "",
    contentType: TemplateContentType.FOLLOWUP,
    subject: "",
    body: "",
  });
  const [loading, setLoading] = useState(false);

  const handleNext = async (updatedForm?: Map) => {
    trackEvent("Create Template Dialog Form - Next Button Pressed", {
      page,
      route: location.pathname,
      "Is First Template": isFirstTemplate,
    });
    if (
      page === Page.fromScratch &&
      !hasSocialMediaProfile(profile) &&
      isDialog &&
      !isPage(routes.onboardingPro)
    ) {
      setPage(Page.socials);
    } else if (
      page === Page.fromScratch ||
      page === Page.socials ||
      page === Page.template
    ) {
      if (isPage(routes.home)) {
        setPage(Page.complete);
      } else {
        setProfile((prev) => ({
          ...prev,
          reviewedFirstTemplate: true,
        }));
        setCreateTemplateOnboardingDialogOpen(false);
        await fetchOutreachTemplates();
        if (isPage(routes.onboardingVaSelfServe)) {
          await fetchOrganizationBulkSendSettings();
          await fetchNiches();
        }
      }
      return;
    }
  };

  const initializeTemplate = async (userInput: string) => {
    setCreatingTemplateLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/init`,
        "POST",
        {},
        {
          type: AgentSessionType.template_onboarding,
          userInput,
        },
      );
      const { outreach, followup } = res;
      setEditTemplate(outreach);
      setFollowUpTemplate(followup);
      setPage(Page.template);
      if (outreach?.agentSessionId) {
        storeInfoFromAgentSession(outreach.agentSessionId);
      }
      if (outreach) {
        addTemplateTaskCompleted();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreatingTemplateLoading(false);
    }
  };

  const storeInfoFromAgentSession = async (agentSessionId: number) => {
    // Update OrganizationProfile info from the background from template content
    if (!agentSessionId) {
      return;
    }

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/store-portfolio`,
        "POST",
      );
    } catch (error) {
      // Do nothing
    }
  };

  const handleBack = async () => {
    trackEvent("Create Template Dialog Form - Back Button Pressed", {
      page,
      route: location.pathname,
      "Is First Template": isFirstTemplate,
    });
    if (page === Page.pitchType) {
      setCreateTemplateOnboardingDialogOpen(false);
      if (!isPage(routes.home)) {
        setProfileDialogOpen(true);
      }
    } else if (page === Page.portfolioLink) {
      setPage(Page.pitchType);
    } else if (page === Page.fromScratch) {
      if (!alreadyShownCopywriterOffer) {
        trackEvent("Copywriter Template Offer - Shown", {
          page,
          route: location.pathname,
        });
        setOpenCopywriterOffer(true);
      } else {
        setPage(Page.pitchType);
      }
    }
    if (page === Page.template) {
      setPage(Page.portfolioLink);
    } else if (page === Page.socials) {
      setPage(Page.fromScratch);
    }
  };

  const handleClose = () => {
    trackEvent("Create Template Dialog Form Closed", {
      page,
      route: location.pathname,
    });
    if (isDirty) {
      setOpenIsDirtyWarning(true);
      return;
    }
    confirmedClose();
  };

  const confirmedClose = () => {
    // Show copywriter offer when user exits during first template creation
    // Show on any page except complete, as long as it's their first template
    if (
      isFirstTemplate &&
      page !== Page.complete &&
      page !== Page.pitchType &&
      !alreadyShownCopywriterOffer
    ) {
      trackEvent("Copywriter Template Offer - Shown", {
        page,
        route: location.pathname,
      });
      setOpenCopywriterOffer(true);
      return;
    }
    finalClose();
  };

  const finalClose = () => {
    setPage(Page.pitchType);
    setCreateTemplateOnboardingDialogOpen(false);
    setAlreadyShownCopywriterOffer(false);
  };

  const saveOrgProfile = async (onboardingForm: Map) => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...onboardingForm,
          page: location.pathname,
        },
      );
      handleNext();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreateTemplateDialogWrapper
      handleClose={handleClose}
      isDialog={isDialog}
      dialogTitle={page === Page.pitchType ? "Create Your Own Template" : ""}
    >
      {creatingTemplateLoading && (
        <TemplateCreationLoading
          creatingTemplateLoading={creatingTemplateLoading}
        />
      )}

      {!creatingTemplateLoading && page === Page.pitchType && (
        <PickTemplateMethodForm
          isDialog={isDialog}
          handleNext={(method?: TemplateMethod | null) => {
            if (method === TemplateMethod.from_scratch) {
              setPage(Page.fromScratch);
            } else {
              setPage(Page.portfolioLink);
            }
          }}
        />
      )}
      {!creatingTemplateLoading && page === Page.portfolioLink && (
        <PortfolioLinkForm
          handleBack={handleBack}
          handleNext={(link, additionalInfo) => {
            let text = `Here is some additional information about me that might be helpful for you to know when creating the template: ${additionalInfo}.`;
            if (link) {
              text = `My portfolio can be found here: ${link}. ` + text;
            }
            initializeTemplate(text);
          }}
        />
      )}
      {!creatingTemplateLoading && page === Page.fromScratch && (
        <EditTemplateDialog
          propsHandleClose={handleBack}
          onSuccess={handleNext}
        />
      )}
      {!creatingTemplateLoading && page === Page.socials && (
        <SocialMediaLinksForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
          handleNext={async () => {
            await saveOrgProfile(onboardingForm);
          }}
          loading={loading}
          handleBack={handleBack}
          handleSkip={handleNext}
        />
      )}
      {!creatingTemplateLoading && page === Page.template && (
        <ReviewBentoAiTemplate
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
          handleBack={handleBack}
          handleNext={handleNext}
          setIsDirty={setIsDirty}
          followUpTemplate={followUpTemplate}
          setFollowUpTemplate={setFollowUpTemplate}
        />
      )}

      {!creatingTemplateLoading && page === Page.complete && (
        <>
          <Typography variant="h4" paragraph sx={{ textAlign: "center" }}>
            <Box
              component="i"
              color="primary.main"
              sx={{ pr: 2 }}
              className="fa-solid fa-party-horn"
            />
            Template Created!
          </Typography>
          <Typography paragraph sx={{ textAlign: "center" }}>
            Congrats on creating your first template! This gives you a
            ready-to-use message that makes reaching out to brands faster and
            easier.
          </Typography>
          <Typography gutterBottom sx={{ textAlign: "center" }}>
            Want create or edit your templates? You can further customize your
            templates in the{" "}
            <Link
              component={RouterLink}
              to={`/${routes.templates}`}
              onClick={() => {
                trackEvent(
                  "Create Template Dialog Form - Open Templates Page",
                  {
                    page,
                    route: location.pathname,
                  },
                );
                setCreateTemplateOnboardingDialogOpen(false);
              }}
              sx={{ textDecoration: "none" }}
            >
              <strong>template page</strong>
            </Link>
            .
          </Typography>
          {isPage(routes.home) && incompleteProfile && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography paragraph variant="h4" sx={{ textAlign: "center" }}>
                Next Step: Complete Your Profile
              </Typography>
              <Typography paragraph sx={{ textAlign: "center" }}>
                To help you pitch brands effectively, fill in a few details to
                get personalized brand recommendations tailored to your content.
              </Typography>
              <Box display="flex" justifyContent="center">
                <Button
                  disableElevation
                  sx={{ textAlign: "center" }}
                  variant="contained"
                  onClick={() => {
                    trackEvent(
                      "Create Template Dialog Form - Complete Profile Started",
                      {
                        page,
                        route: location.pathname,
                      },
                    );
                    setCreateTemplateOnboardingDialogOpen(false);
                    setProfileDialogOpen(true);
                  }}
                >
                  Start
                </Button>
              </Box>
            </>
          )}
          {isPage(routes.home) && !incompleteProfile && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography paragraph variant="h4" sx={{ textAlign: "center" }}>
                Next Step: Send Your First Pitch
              </Typography>
              <Typography paragraph sx={{ textAlign: "center" }}>
                To help you pitch brands effectively, let's guide you through
                sending your first brand pitch using Bento.
              </Typography>
              <Box display="flex" justifyContent="center">
                <Button
                  disableElevation
                  sx={{ textAlign: "center" }}
                  variant="contained"
                  onClick={() => {
                    trackEvent(
                      "Create Template Dialog Form - Send First Email Started",
                      {
                        page,
                        route: location.pathname,
                      },
                    );
                    setCreateTemplateOnboardingDialogOpen(false);
                    setEmailBrandDialogOpen(true);
                  }}
                >
                  Start
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      <UnsavedChanges
        isUserFirstTemplate={isFirstTemplate}
        confirmedClose={confirmedClose}
        setIsDirty={setIsDirty}
        setOpenIsDirtyWarning={setOpenIsDirtyWarning}
        openIsDirtyWarning={openIsDirtyWarning}
      />

      <CopywriterOfferDialog
        open={openCopywriterOffer}
        handleClose={() => setOpenCopywriterOffer(false)}
        setAlreadyShownCopywriterOffer={setAlreadyShownCopywriterOffer}
        onDecline={() => {
          setOpenCopywriterOffer(false);
          setAlreadyShownCopywriterOffer(true);
          if (page === Page.pitchType) {
            finalClose();
          }
        }}
      />
    </CreateTemplateDialogWrapper>
  );
};

export default CreateTemplateDialog;
